/* begin general import */
import { openOnRowClick } from "helpers/row-click";
import { DownOutlined } from "@ant-design/icons";
import { StringFilter } from "@react3l/advanced-filters";
import { Button, Card, Col, Dropdown, Menu, Row, Tooltip } from "antd";
import Table, { ColumnProps } from "antd/lib/table";
import classNames from "classnames";
/* end general import */
/* begin filter import */
import AdvanceStringFilter from "components/Utility/AdvanceFilter/AdvanceStringFilter/AdvanceStringFilter";
import InputSearch from "components/Utility/InputSearch/InputSearch";
import Pagination from "components/Utility/Pagination/Pagination";
import { formatDate } from "helpers/date-time";
import { Certification, CertificationFilter } from "models/Certification";
import { Moment } from "moment";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
/* end filter import */
/* begin individual import */
import { certificationRepository } from "repositories/certification-repository";
import detailService from "services/pages/detail-service";
import masterService from "services/pages/master-service";
import { getAntOrderType } from "services/table-service";
import nameof from "ts-nameof.macro";
import CertificationDetailModal from "../CertificationDetail/CertificationDetailModal";
import CertificationPreview from "./CertificationPreview";
import "./CertificationMaster.scss";
import moment from "moment";
/* end individual import */

function CertificationMaster() {
  const [translate] = useTranslation();

  const {
    list,
    total,
    loadingList,
    filter,
    toggle,
    handleChangeFilter,
    handleToggleSearch,
    handleTableChange,
    handlePagination,
    handleServerDelete,
    handleSearch,
    handleUpdateNewFilter,
    handleImportList,
    handleListExport,
    handleExportTemplateList,
    importButtonRef,
  } = masterService.useMaster<Certification, CertificationFilter>(
    CertificationFilter,
    "",
    certificationRepository.list,
    certificationRepository.count,
    certificationRepository.delete,
    certificationRepository.bulkDelete
  );

  const {
    isOpenPreview,
    isLoadingPreview,
    previewModel,
    handleOpenPreview,
    handleClosePreview,
  } = masterService.usePreview<Certification>(Certification, certificationRepository.get);

  const {
    model,
    isOpenDetailModal,
    isDetailModal,
    handleOpenDetailModal,
    handleCloseDetailModal,
    handleSaveModel,
    loadingModel,
    handleChangeSimpleField,
    handleChangeObjectField,
    dispatch,
    handleUpdateNewModel,
  } = detailService.useDetailModal(
    Certification,
    certificationRepository.get,
    certificationRepository.save,
    handleSearch
  );

  const _handleChangeFilter = React.useCallback(
    (value) => {
      const newFilter = { ...filter };
      newFilter["search"] = value;
      handleUpdateNewFilter(newFilter);
    },
    [filter, handleUpdateNewFilter]
  );

  const handleGoDetail = React.useCallback(
    (id: number) => () => {
      handleClosePreview();
      handleOpenDetailModal(id);
    },
    [handleClosePreview, handleOpenDetailModal]
  );

  const [dropdown, setDropdown] = React.useState<boolean>(false);

  const handleDropdown = React.useCallback(() => {
    setDropdown(!dropdown);
  }, [dropdown]);

  const menuFilter = React.useMemo(
    () => (
      <Menu>
        <Menu.Item key="2">
          <Tooltip title={translate("general.button.importExcel")}>
            <>
              <input
                ref={importButtonRef}
                type="file"
                style={{ display: "none" }}
                id="master-import"
                onChange={handleImportList(certificationRepository.import)}
              />
              <button
                className="btn border-less gradient-btn-icon grow-animate-2"
                onClick={() => {
                  importButtonRef.current.click();
                }}
              >
                <i className="tio-file_add_outlined" />
              </button>
            </>
          </Tooltip>
        </Menu.Item>
        <Menu.Item key="3">
          <Tooltip title={translate("general.button.exportExcel")}>
            <button
              className="btn border-less gradient-btn-icon grow-animate-2"
              onClick={handleListExport(filter, certificationRepository.export)}
            >
              <i className="tio-file_outlined" />
            </button>
          </Tooltip>
        </Menu.Item>
        <Menu.Item key="4">
          <Tooltip title={translate("general.button.downloadTemplate")}>
            <button
              className="btn border-less gradient-btn-icon grow-animate-2"
              onClick={handleExportTemplateList(
                certificationRepository.exportTemplate
              )}
            >
              <i className="tio-download_to" />
            </button>
          </Tooltip>
        </Menu.Item>
      </Menu>
    ),
    [
      translate,
      importButtonRef,
      handleImportList,
      handleListExport,
      filter,
      handleExportTemplateList,
    ]
  );

  const menuAction = React.useCallback(
    (id: number, certification: Certification) => (
      <Menu>
        <Menu.Item key="1">
          <div className="ant-action-menu" onClick={handleOpenPreview(id)}>
            {translate("general.actions.view")}
          </div>
        </Menu.Item>
        <Menu.Item key="2">
          <div className="ant-action-menu" onClick={handleGoDetail(id)}>
            {translate("general.actions.edit")}
          </div>
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="3" danger>
          <div
            className="ant-action-menu"
            onClick={() => handleServerDelete(certification)}
          >
            {translate("general.actions.delete")}
          </div>
        </Menu.Item>
      </Menu>
    ),
    [handleGoDetail, handleOpenPreview, handleServerDelete, translate]
  );

  const columns: ColumnProps<Certification>[] = useMemo(
    () => [
      {
        title: (
          <div className="text-center gradient-text">
            {translate("certifications.name")}
          </div>
        ),
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        sorter: true,
        sortOrder: getAntOrderType<Certification, CertificationFilter>(
          filter,
          nameof(list[0].name)
        ),
        width: 100,
        render(name: string) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames("cell-master__first-row", {
                  "first-row--ellipsis": name && name.length >= 30,
                })}
              >
                {name}
              </div>
            </div>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">
            {translate("certifications.validFrom")}
          </div>
        ),
        key: nameof(list[0].validFrom),
        dataIndex: nameof(list[0].validFrom),
        sorter: true,
        sortOrder: getAntOrderType<Certification, CertificationFilter>(
          filter,
          nameof(list[0].validFrom)
        ),
        width: 100,
        align: "center",
        render(_validFrom: Moment, cert: Certification) {
          return (
            <div className="ant-cell-master__container">
              <div className={classNames("cell-master__first-row")}>
                {formatDate(cert.createdAt)}
              </div>
            </div>
          );
        },
      },

      {
        title: (
          <div className="text-center gradient-text">
            {translate("certifications.validUntil")}
          </div>
        ),
        key: nameof(list[0].validUntil),
        dataIndex: nameof(list[0].validUntil),
        sorter: true,
        sortOrder: getAntOrderType<Certification, CertificationFilter>(
          filter,
          nameof(list[0].validUntil)
        ),
        width: 100,
        align: "center",
        render(_validUntil: Moment, cert: Certification) {
          return (
            <div className="ant-cell-master__container">
              <div className={classNames("cell-master__first-row")}>
                {formatDate(moment(cert.createdAt).add(20, 'years'))}
              </div>
            </div>
          );
        },
      },


      {
        title: (
          <div className="text-center gradient-text">
            {translate("certifications.createdAt")}
          </div>
        ),
        key: nameof(list[0].createdAt),
        dataIndex: nameof(list[0].createdAt),
        sorter: true,
        sortOrder: getAntOrderType<Certification, CertificationFilter>(
          filter,
          nameof(list[0].createdAt)
        ),
        width: 100,
        align: "center",
        render(createdAt: Moment) {
          return (
            <div className="ant-cell-master__container">
              <div className={classNames("cell-master__first-row")}>
                {formatDate(createdAt)}
              </div>
            </div>
          );
        },
      },

      {
        title: (
          <div className="text-center gradient-text">
            {translate("general.actions.action")}
          </div>
        ),
        key: "action",
        dataIndex: nameof(list[0].id),
        fixed: "right",
        width: 80,
        align: "center",
        render(id: number, certification: Certification) {
          return (
            <div className="d-flex justify-content-center button-action-table">
              <Dropdown
                overlay={menuAction(id, certification)}
                trigger={["click"]}
                placement="bottomCenter"
                arrow
              >
                <span className="action__dots">...</span>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [translate, list, filter, menuAction]
  );

  const filterChildren = useMemo(
    () => (
      <>
        <Row className="mt-4">
          <Col lg={4} className="pr-4">
            <label className="label">{translate("certifications.name")}</label>
            <AdvanceStringFilter
              isMaterial={true}
              value={filter[nameof(list[0].name)]["contain"]}
              onEnter={handleChangeFilter(
                nameof(list[0].name),
                "contain" as any,
                StringFilter
              )}
              placeHolder={translate("certifications.placeholder.name")}
            />
          </Col>
        </Row>
      </>
    ),
    [filter, handleChangeFilter, list, translate]
  );

  return (
    <>
      <div className="page page__master certification__container">
        <div className="page__header d-flex align-items-center justify-content-between">
          <div className="page__title">
            {translate("certifications.master.title")}
          </div>
        </div>
        <div className="page__search">
          <Card bordered={false}>
            <div className="d-flex align-items-center">
              <div className="d-flex flex-grow-1">
                <div className="pr-4 w70">
                  <InputSearch
                    value={filter["search"]}
                    onChange={_handleChangeFilter}
                    placeHolder="Tìm kiếm"
                  />
                </div>

                <button
                  className={classNames(
                    "btn component__btn-toggle mr-3 grow-animate-1",
                    toggle === true ? "component__btn-toggle-active" : ""
                  )}
                  onClick={handleToggleSearch}
                >
                  <i className="tio-tune_horizontal"></i>
                  <span className="component_btn-text">
                    {translate("general.button.advance")}
                  </span>
                </button>
              </div>

              <div className="d-flex justify-content-around ml-4">
                <div className="table__action">
                  <Dropdown overlay={menuFilter} trigger={["click"]}>
                    <Button onClick={handleDropdown}>
                      <span className="component_btn-text">
                        {translate("general.actions.action")}
                      </span>
                      <DownOutlined className={dropdown ? "dropdown" : null} />
                    </Button>
                  </Dropdown>
                </div>
              </div>
            </div>
            <CSSTransition
              in={toggle}
              timeout={100}
              classNames={"show"}
              unmountOnExit
            >
              {filterChildren}
            </CSSTransition>
          </Card>
        </div>
        <div className="page__master-table custom-scrollbar pb-4">
          <Card bordered={false}>
            <Table
              onRow={(record) => openOnRowClick(handleOpenPreview(record.id))}
              rowKey={nameof(list[0].id)}
              columns={columns}
              pagination={false}
              dataSource={list}
              loading={loadingList}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              title={() => (
                <>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="action__result">{total} Kết quả</span>
                    <div className="flex-shrink-1 d-flex align-items-center">
                      <Pagination
                        skip={filter.skip}
                        take={filter.take}
                        total={total}
                        onChange={handlePagination}
                        style={{ margin: "10px" }}
                      />
                    </div>
                  </div>
                </>
              )}
            />
          </Card>
        </div>
      </div>
      <CertificationPreview
        previewModel={previewModel}
        isOpenPreview={isOpenPreview}
        isLoadingPreview={isLoadingPreview}
        handleClosePreview={handleClosePreview}
        handleGoDetail={handleGoDetail}
        translate={translate}
      />
      <CertificationDetailModal
        model={model}
        isDetail={isDetailModal}
        visible={isOpenDetailModal}
        handleSave={handleSaveModel}
        handleCancel={handleCloseDetailModal}
        onChangeSimpleField={handleChangeSimpleField}
        onChangeObjectField={handleChangeObjectField}
        dispatchModel={dispatch}
        loading={loadingModel}
        visibleFooter={true}
        handleUpdateNewModel={handleUpdateNewModel}
      />
    </>
  );
}

export default CertificationMaster;

